<template>
  <aside class="results" v-if="poll">
    <header>
      <h3>Results</h3>
      <div class="head-right">
        <span class="muted">
          {{ total }} vote{{ total === 1 ? '' : 's' }}
          <template v-if="updatedAt"> · {{ updatedAt }}</template>
        </span>
        <button
          type="button"
          class="refresh"
          :class="{ busy: loading }"
          :disabled="loading || clearing || !canLoad"
          title="Refresh results"
          aria-label="Refresh results"
          @click="refresh"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M20 12a8 8 0 1 1-2.34-5.66"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              d="M20 3v5h-5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </header>

    <p v-if="scopeError" class="error">{{ scopeError }}</p>
    <p v-else-if="!canLoad" class="muted hint">
      Select a college first.
      <router-link to="/admin">Facilitator hub</router-link>
    </p>
    <ul v-else>
      <li v-for="opt in poll.options" :key="opt.id">
        <div class="row">
          <span>{{ opt.label }}</span>
          <strong>{{ counts[opt.id] || 0 }}</strong>
        </div>
        <div class="bar-track">
          <div class="bar-fill" :style="{ width: pct(opt.id) + '%' }" />
        </div>
      </li>
    </ul>
    <button
      type="button"
      class="btn btn-secondary clear-btn"
      :disabled="loading || clearing || !canLoad || !total"
      @click="clearVotes"
    >
      {{ clearing ? 'Clearing…' : 'Clear votes for this poll' }}
    </button>
  </aside>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { getAdminSession } from '../services/adminSession'
import { clearPollResults, getPollResults } from '../services/api'

const POLL_CLEARED_EVENT = 'u80-poll-cleared'
const SESSION_EVENT = 'u80-session-changed'

const props = defineProps({
  slideId: { type: String, required: true },
  poll: { type: Object, required: true },
})

const collegeName = ref('')
const collegeId = ref('')
const counts = ref({})
const total = ref(0)
const loading = ref(false)
const clearing = ref(false)
const scopeError = ref('')
const updatedAt = ref('')

const canLoad = computed(() => !!(collegeId.value || collegeName.value))

function pct(id) {
  if (!total.value) return 0
  return Math.round(((counts.value[id] || 0) / total.value) * 100)
}

async function clearVotes() {
  if (!canLoad.value || clearing.value) return
  const where = collegeName.value || 'this college'
  const ok = confirm(`Clear votes for this poll at ${where}? Students can vote again.`)
  if (!ok) return
  clearing.value = true
  scopeError.value = ''
  try {
    await clearPollResults(props.slideId, {
      collegeId: collegeId.value || undefined,
      collegeName: collegeName.value,
    })
    counts.value = {}
    total.value = 0
    updatedAt.value = new Date().toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    try {
      window.dispatchEvent(
        new CustomEvent(POLL_CLEARED_EVENT, { detail: { slideId: props.slideId } }),
      )
    } catch {
      /* ignore */
    }
  } catch (e) {
    scopeError.value = e.message || 'Could not clear votes'
  } finally {
    clearing.value = false
  }
}

// Manual refresh only — polling every 2s was too many API calls per session.
async function refresh() {
  if (!canLoad.value) return
  loading.value = true
  scopeError.value = ''
  try {
    const res = await getPollResults(props.slideId, {
      collegeId: collegeId.value || undefined,
      collegeName: collegeName.value,
    })
    collegeId.value = res.collegeId || collegeId.value
    counts.value = res.counts || {}
    total.value = res.total || 0
    updatedAt.value = new Date().toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch (e) {
    counts.value = {}
    total.value = 0
    scopeError.value = e.message || 'Could not load results'
  } finally {
    loading.value = false
  }
}

async function loadScope() {
  const viewing = getAdminSession()
  collegeName.value = viewing.collegeName || ''
  collegeId.value = viewing.collegeId || ''
  if (canLoad.value) await refresh()
}

onMounted(() => {
  loadScope()
  window.addEventListener(SESSION_EVENT, loadScope)
})
onUnmounted(() => {
  window.removeEventListener(SESSION_EVENT, loadScope)
})
watch(() => props.slideId, refresh)
</script>

<style scoped>
.results {
  background: var(--cream);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 1rem 1.15rem;
  box-shadow: var(--shadow-soft);
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

h3 {
  margin: 0;
  font-size: 1rem;
}

.head-right {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.clear-btn {
  width: 100%;
  margin-top: 1rem;
}

.refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 50%;
  background: var(--paper-warm);
  color: var(--muted);
  cursor: pointer;
}

.refresh:hover:not(:disabled) {
  color: var(--teal);
  border-color: var(--teal);
}

.refresh:disabled {
  cursor: default;
}

.refresh svg {
  width: 0.95rem;
  height: 0.95rem;
}

.refresh.busy svg {
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.hint {
  margin: 0;
  font-size: 0.85rem;
}

.error {
  color: var(--danger);
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
}

ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.bar-track {
  height: 8px;
  background: var(--paper-warm);
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: var(--teal-bright);
  transition: width 0.3s ease;
}
</style>
